import type { ProjectData, ProjectNode } from '@/shared/types/projectData';

interface ResolveAuthoritativeProjectDataParams {
  singleProjectData: ProjectData | null | undefined;
  aggregatedData: ProjectData | null | undefined;
}

interface ShouldAdoptAuthoritativeProjectDataParams {
  previousData: ProjectData | null;
  authoritativeData: ProjectData;
}

function decorateSingleProjectAuthoritativeData(singleProjectData: ProjectData): ProjectData {
  let changed = false;
  const decoratedNodes = singleProjectData.nodes.map((node) => {
    const nextProjectId =
      typeof node.projectId === 'string' && node.projectId.trim().length > 0
        ? node.projectId
        : singleProjectData.projectId;
    const nextProjectClient =
      node.projectClient !== undefined
        ? node.projectClient
        : singleProjectData.client;

    if (nextProjectId === node.projectId && nextProjectClient === node.projectClient) {
      return node;
    }

    changed = true;
    return {
      ...node,
      projectId: nextProjectId,
      ...(nextProjectClient !== undefined ? { projectClient: nextProjectClient } : {}),
    };
  });

  if (!changed) {
    return singleProjectData;
  }

  return {
    ...singleProjectData,
    nodes: decoratedNodes,
  };
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${key}:${stableStringify(record[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
}

function arrayKey(value: unknown): string {
  return Array.isArray(value) ? value.map((item) => String(item)).join('\u001f') : '';
}

function nodeAdoptionKey(node: ProjectNode): string {
  return [
    node.id,
    node.type,
    node.title,
    node.description || '',
    node.parentId || '',
    String(node.order ?? ''),
    node.completionState || '',
    node.status || '',
    node.priority || '',
    String(node.progress ?? ''),
    node.assignee || '',
    arrayKey(node.assignees),
    arrayKey(node.mentions),
    arrayKey(node.assigneeDisplayNames),
    node.date || '',
    node.startDate || '',
    node.endDate || '',
    node.dueDate || '',
    arrayKey(node.dependencies),
    arrayKey(node.linkedDocs),
    arrayKey(node.tags),
    node.category || '',
    node.workstream || '',
    arrayKey(node.developmentRoles),
    node.visibilityTier || '',
    arrayKey(node.visibleToAccountIds),
    node.classificationSource || '',
    String(node.classificationConfidence ?? ''),
    node.projectClient || '',
    node.projectId || '',
    node.color || '',
    node.icon || '',
    node.content || '',
    arrayKey(node.attachments),
    String(node.attachmentCount ?? ''),
    String(Boolean(node.hasImages)),
    String(Boolean(node.hasDocuments)),
    stableStringify(node.checklist),
    stableStringify(node.viewState),
    node.createdAt || '',
    node.completedAt || '',
    node.createdBy || '',
    node.updatedBy || '',
    stableStringify(node.customFields),
  ].join('\u001e');
}

function projectDataAdoptionKey(projectData: ProjectData): string {
  return [
    projectData.projectId,
    projectData.projectName,
    projectData.description || '',
    projectData.client || '',
    projectData.projectAssigneeId || '',
    projectData.companyName || '',
    projectData.projectSlug || '',
    projectData.status || '',
    arrayKey(projectData.rootNodeIds),
    stableStringify(projectData.kanbanColumns),
    stableStringify(projectData.files),
    stableStringify(projectData.metadata?.settings),
  ].join('\u001d');
}

function hasEquivalentProjectDataForAdoption(previousData: ProjectData, authoritativeData: ProjectData): boolean {
  if (projectDataAdoptionKey(previousData) !== projectDataAdoptionKey(authoritativeData)) {
    return false;
  }

  if (previousData.nodes.length !== authoritativeData.nodes.length) {
    return false;
  }

  const previousNodesById = new Map(previousData.nodes.map((node) => [node.id, node]));
  for (const authoritativeNode of authoritativeData.nodes) {
    const previousNode = previousNodesById.get(authoritativeNode.id);
    if (!previousNode || nodeAdoptionKey(previousNode) !== nodeAdoptionKey(authoritativeNode)) {
      return false;
    }
  }

  return true;
}

export function resolveAuthoritativeProjectData({
  singleProjectData,
  aggregatedData,
}: ResolveAuthoritativeProjectDataParams): ProjectData | null | undefined {
  if (singleProjectData) {
    return decorateSingleProjectAuthoritativeData(singleProjectData);
  }

  return aggregatedData;
}

export function shouldAdoptAuthoritativeProjectData({
  previousData,
  authoritativeData,
}: ShouldAdoptAuthoritativeProjectDataParams): boolean {
  if (!previousData) {
    return true;
  }

  if (previousData === authoritativeData) {
    return false;
  }

  // WebSocket operation-result already patches the mounted watcher state. The
  // MultiProject cache then emits a distinct authoritative object for the same
  // data; adopting that equivalent object immediately remounts the Gantt state
  // and shows up as a second-browser flicker. Still adopt when any visible,
  // permission, ordering, or metadata field actually diverges.
  return !hasEquivalentProjectDataForAdoption(previousData, authoritativeData);
}
